import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Image from 'next/image'
import { programLink } from '~shared/utils/program-link'
import { Program } from '~shared/api/categories/types'
import { cn } from '~shared/utils/cn'
import { JournalRelatedCoursesDialog } from '~pages/journal-page/journal-related-courses-dialog'

export const RelatedCoursesBanner = ({
  programs,
  oneArticle,
  asBanner
}: {
  programs: Program[] | null
  oneArticle?: boolean
  asBanner?: boolean
}) => {
  const router = useRouter()
  const [isVisible, setIsVisible] = useState(true)
  const [randomNumber] = useState(Math.floor(Math.random() * 12) + 1)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [hideByFooter, setHideByFooter] = useState(false)

  useEffect(() => {
    if (!asBanner) return

    let footer: HTMLElement | null = null

    const updateFooter = () => {
      footer = document.querySelector('footer')
    }

    const handleScroll = () => {
      if (!footer) return
      setHideByFooter(footer.getBoundingClientRect().top <= 130)
    }

    updateFooter()
    router.events.on('routeChangeComplete', updateFooter)
    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => {
      router.events.off('routeChangeComplete', updateFooter)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [asBanner, router])

  const getFallbackImage = (idx: number) => {
    const n = randomNumber + idx
    return `/assets/imgs/groups/${n < 13 ? n : randomNumber - idx}.png`
  }

  // ─── oneArticle режим ────────────────────────────────────────────────────────
  if (oneArticle) {
    return (
      <div className='flex flex-col gap-[8px]'>
        {programs?.map((program, index) => (
          <div
            key={`RelatedCourses-OneArticle__Div${index + 1}`}
            className={cn(
              'flex gap-4 p-8 box-border max-w-full relative bg-[#F7F7F7] rounded-[16px]',
              'flex-col items-start w-[343px] h-[288px]',
              'sm:flex-row sm:items-center sm:w-[640px] sm:h-[136px]',
              'lg:w-[800px]'
            )}>
            <div className='relative flex-shrink-0 w-20 h-20'>
              <Image
                sizes='80px'
                quality={50}
                alt={`Program-Category__${index + 1}`}
                src={
                  program?.image?.url ||
                  `/assets/imgs/groups/${Math.floor(Math.random() * 12) + 1}.png`
                }
                fill
                className='object-cover rounded-[16px]'
              />
            </div>
            <div
              className={cn(
                'flex justify-between w-full h-full items-start flex-col sm:flex-row sm:items-center'
              )}>
              <div className='flex flex-col'>
                <p className='text-sm font-medium'>Программы по теме</p>
                <p className='mt-2 font-medium'>
                  {index === 0 && program?.name}
                </p>
              </div>
              <Link
                href={programLink(program)}
                className='text-[var(--alpha)] underline underline-offset-2 hover:decoration-[var(--omega)] text-sm'>
                Узнать больше
              </Link>
            </div>
          </div>
        ))}
      </div>
    )
  }

  // ─── Sidebar режим ───────────────────────────────────────────────────────────
  if (!isVisible) return null

  return (
    <>
      <div
        className={cn(
          'hidden lg:flex',
          'flex-col items-start gap-6 rounded-[16px]',
          'bg-[var(--omega)] text-[var(--alpha)]',
          'p-4 2lg:p-8'
        )}>
        <div className='flex w-full items-center'>
          <div className='flex items-center justify-center px-4 py-2 bg-[#3A52EE] min-w-fit rounded-[8px]'>
            <span className='text-sm text-white'>Программы по теме</span>
          </div>
          <button
            onClick={() => setIsVisible(false)}
            className='ml-auto w-6 h-6 flex items-center justify-center rounded-full bg-[var(--alpha)] hover:opacity-70 text-black border-0 cursor-pointer'
            aria-label='Закрыть'>
            ✕
          </button>
        </div>

        {programs?.map((program, idx) => (
          <React.Fragment key={program?.slug}>
            <div
              className={cn(
                'flex gap-4 text-[var(--alpha)] min-h-[4rem]',
                'flex-row flex-wrap sm:flex-nowrap'
              )}>
              <div
                className={cn(
                  'relative flex-shrink-0 overflow-hidden rounded-[16px]',
                  'w-12 h-12'
                )}>
                <Image
                  src={ getFallbackImage(idx)}
                  fill
                  alt=''
                  quality={50}
                  sizes='80px'
                  className='object-cover'
                />
              </div>
              <div className={cn('flex gap-4 w-full items-start flex-col')}>
                <div>
                  <span className='block'>{program.name}</span>
                </div>

                <Link
                  href={programLink(program)}
                  className='text-[var(--alpha)] underline underline-offset-2 hover:decoration-[var(--omega)] text-sm'>
                  Узнать больше
                </Link>
              </div>
            </div>

          </React.Fragment>
        ))}

        <span className='text-[13px]/[150%] font-medium tracking-[0.4px] text-[#8B8B8B] mt-4'>
          Спешите записаться на обучение, количество мест ограничено
        </span>
      </div>


        <div className='lg:hidden'>
          <div
            className={cn(
              'fixed bottom-[140px] left-0 w-full transition-transform duration-500',
              // скрываем у футера
              hideByFooter ? '-translate-y-full' : 'translate-y-0',
            )}>
            <div className='container container-slim box-border my-[4px]'>
              <div className='bg-[#dde4fb] flex items-center justify-between px-[16px] py-[10px] rounded-[16px] gap-[8px]'>
                <span className='text-sm font-medium text-accent truncate'>
                  Курсы по теме
                </span>

                <div className='flex items-center gap-[8px] flex-shrink-0'>
                  <button
                    onClick={() => setDialogOpen(true)}
                    className={cn(
                      'bg-accent text-white rounded-[16px] border-0',
                      'h-[38px] px-[16px] text-[12px] font-medium leading-[120%]',
                      'flex items-center justify-center cursor-pointer',
                      'whitespace-nowrap'
                    )}>
                    Узнать больше
                  </button>
                  <button
                    onClick={() => setIsVisible(false)}
                    className='bg-[var(--alpha)] text-[var(--omega)] rounded-full w-[26px] h-[26px] flex items-center justify-center text-[11px] flex-shrink-0 hover:opacity-70 border-0 cursor-pointer'
                    aria-label='Закрыть'>
                    ✕
                  </button>
                </div>
              </div>
            </div>
          </div>

          {programs && (
            <JournalRelatedCoursesDialog
              programs={programs}
              open={dialogOpen}
              onOpenChange={setDialogOpen}
            />
          )}
        </div>
    </>
  )
}
